import LegalLayout from '@/components/layout/LegalLayout';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function TermsPage() {
  useSEO({
    title: 'Terms of Service | Acodia',
    description: 'The terms under which Acodia Ltd provides the Acodia Watch security monitoring service. Governed by the laws of England and Wales.',
    path: '/terms',
    ogImage: `${SITE_URL}/images/og/og-default.png`,
  });

  return (
    <LegalLayout eyebrow="Legal" title="Terms of Service" lastUpdated="26 May 2026" testId="terms-page">
      <h2>1. Agreement to Terms</h2>
      <p>
        These Terms of Service ("Terms") govern your use of the Acodia Watch service and website provided by
        <strong> Acodia Ltd</strong>, a company registered in England and Wales under Companies House number
        15412512, registered office 20 Wenlock Road, London, N1 7GU ("Acodia", "we", "us"). By creating an
        account, subscribing, or using the service, you agree to these Terms. If you do not agree, do not use
        the service.
      </p>

      <h2>2. The Service</h2>
      <p>
        Acodia Watch provides continuous security monitoring of web domains you own or are authorised to
        manage. It analyses publicly visible security signals — including SSL/TLS configuration, HTTP security
        headers, DNS records (such as SPF and DMARC), software fingerprints and associated known
        vulnerabilities (CVEs), certificate expiry, and public blacklist status — and generates
        recommendations, alerts, and scan history. Acodia Watch is a monitoring and advisory tool; it does not
        modify, patch, or defend your systems on your behalf, and it is not a guarantee against compromise.
      </p>

      <h2>3. Account Registration</h2>
      <p>
        You must provide accurate information and keep your credentials secure. You are responsible for all
        activity under your account. You must be at least 18 years old and using the service for business or
        professional purposes. Notify us promptly of any unauthorised use.
      </p>

      <h2>4. Subscriptions and Payments</h2>
      <p>
        Paid plans are billed in advance on a recurring basis (monthly or annually) through our payment
        processor, Stripe. By subscribing you authorise recurring charges until you cancel. Fees are stated on
        our pricing page and exclude any applicable taxes unless stated. We may change pricing on renewal with
        reasonable notice. Except where required by law, fees already paid are non-refundable. You may cancel
        at any time; your subscription remains active until the end of the current billing period.
      </p>

      <h2>5. Domain Ownership and Verification</h2>
      <p>
        You may only monitor domains that you own or are explicitly authorised to manage. We require you to
        verify control of a domain (via DNS, HTML file, or meta tag) before monitoring begins. You represent
        and warrant that you have the right to have each monitored domain scanned. You must not use the
        service against domains you do not control.
      </p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the service against any domain you do not own or have authorisation to test.</li>
        <li>Attempt to disrupt, overload, reverse-engineer, or gain unauthorised access to the service.</li>
        <li>Use the service for any unlawful purpose, or to violate the rights of others.</li>
        <li>Resell or provide the service to third parties except as expressly permitted.</li>
      </ul>

      <h2>7. Intellectual Property</h2>
      <p>
        The service, including its software, content, and branding, is owned by Acodia and protected by
        intellectual property laws. We grant you a limited, non-exclusive, non-transferable right to use the
        service during your subscription. Scan results and reports generated for your domains are yours to use
        for your own business purposes.
      </p>

      <h2>8. Confidentiality</h2>
      <p>
        Each party may receive confidential information from the other. Both parties agree to protect such
        information and use it only to perform under these Terms. Your scan results are treated as
        confidential and are not disclosed to third parties except subprocessors necessary to provide the
        service (see our <a href="/privacy">Privacy Policy</a>) or where required by law.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The service is provided "as is" and "as available". To the maximum extent permitted by law, we
        disclaim all warranties, express or implied, including fitness for a particular purpose. Acodia Watch
        identifies a defined set of publicly observable security signals; it does not detect every
        vulnerability and does not guarantee that a monitored domain is secure. You remain responsible for the
        security of your own systems.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        Nothing in these Terms limits liability that cannot be limited by law (including for death or personal
        injury caused by negligence, or fraud). Subject to that, to the maximum extent permitted by law our
        total aggregate liability arising out of or relating to the service shall not exceed the total fees you
        paid to us in the <strong>twelve (12) months</strong> preceding the event giving rise to the claim. We
        are not liable for indirect, incidental, special, or consequential losses, or for loss of profits,
        data, or goodwill.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify and hold Acodia harmless from claims, damages, and reasonable costs arising
        from your breach of these Terms, your misuse of the service, or your monitoring of domains you were
        not authorised to test.
      </p>

      <h2>12. Termination</h2>
      <p>
        You may cancel at any time from your dashboard. We may suspend or terminate your access if you breach
        these Terms, fail to pay, or use the service unlawfully. On termination, your right to use the service
        ends; we will handle your data as described in our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify you of material changes and update the
        "Last updated" date. Continued use after changes take effect constitutes acceptance.
      </p>

      <h2>14. Governing Law and Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of <strong>England and Wales</strong>. The courts of England and
        Wales have exclusive jurisdiction over any dispute arising out of or relating to these Terms or the
        service.
      </p>

      <h2>15. Contact</h2>
      <p>
        Acodia Ltd, 20 Wenlock Road, London, N1 7GU, United Kingdom.<br />
        Companies House: 15412512<br />
        Email: <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>
      </p>
      <hr />
      <p>Have questions? Email <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.</p>
    </LegalLayout>
  );
}
